import React, { SyntheticEvent, useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";

import {
  IDepartureTime,
  IDepartureTimeRange,
  filterSelector,
  initialDepartureTimeRangeObj,
  changeDepartureTime,
} from "redux/filters";

import { useTranslations } from "hooks/useTranslations";
import { IDepartureTimeFilter } from "hooks/translations.interfaces";
import { searchQuerySelector } from "redux/searchQuery";
import {
  deepCloneObject,
  isDiff,
  minutesToClock,
  pushToDataLayer,
} from "@thekilroygroup/shared-ui";
import {
  Box,
  Button,
  Collapse,
  Slider,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";

export default function DepartureTimeFilter() {
  const { transitions } = useTheme();
  const dispatch = UseAppDispatch();
  const { flights, tripType } = useSelector(searchQuerySelector);
  const { departureTime } = useSelector(filterSelector);
  const ranges = departureTime.departureTimeRanges;

  const { translations } = useTranslations();
  const {
    arrivalTitle,
    departureTitle,
    outboundText,
    returnText,
    multiFlightText,
  }: IDepartureTimeFilter = deepCloneObject(
    translations.filters.filterTypes.filter((obj) => {
      return obj.filter === "departureTime";
    })[0],
  );

  const translationsFilters = translations.filters;
  useEffect(() => {
    updateFilterAmount();
  }, [flights.length, tripType]);

  const [departureTimeState, setDepartureTimeState] = useState(
    departureTime.departureTimeRanges,
  );

  useEffect(() => {
    setDepartureTimeState(ranges);
  }, [departureTime]);

  const updateFilterAmount = () => {
    let nextDepartureTime: IDepartureTime = deepCloneObject(departureTime);
    let nextRanges: IDepartureTimeRange[] =
      nextDepartureTime.departureTimeRanges;

    if (tripType === "ONE_WAY" && ranges.length > 1) {
      nextRanges.length = 1;
    } else if (tripType === "RETURN") {
      nextRanges.length = 2;
      if (typeof nextRanges[1] === "undefined") {
        nextRanges[1] = initialDepartureTimeRangeObj;
      }
    } else {
      nextRanges.length = flights.length;
      flights.forEach((flight, index) => {
        if (typeof nextRanges[index] === "undefined") {
          nextRanges[index] = initialDepartureTimeRangeObj;
        }
      });
    }
    nextDepartureTime.departureTimeRanges = nextRanges;

    if (isDiff(nextDepartureTime, departureTime)) {
      setDepartureTimeState(nextDepartureTime.departureTimeRanges);
      dispatch(changeDepartureTime(nextDepartureTime));
    }
  };

  const handleChangeSlider = (
    event: Event,
    newValue: number | number[],
    filterIndex: number,
  ) => {
    if (typeof newValue === "object") {
      let departureTimeRanges: IDepartureTimeRange[] =
        deepCloneObject(departureTimeState);
      if (departureTimeRanges[filterIndex].showDeparture) {
        departureTimeRanges[filterIndex].departure = newValue;
      } else {
        departureTimeRanges[filterIndex].arrival = newValue;
      }
      setDepartureTimeState(departureTimeRanges);
    }
  };

  const handleChangeSliderComitted = (
    event: Event | SyntheticEvent<Element, Event>,
    newValue: number | number[],
    filterIndex: number,
  ) => {
    let isTouched = false;
    departureTimeState.forEach((range, index) => {
      if (
        range.departure[0] !== 0 ||
        range.arrival[0] !== 0 ||
        range.departure[1] !==
          Number(translationsFilters.filterValues.maxDepartureTime) ||
        range.arrival[1] !==
          Number(translationsFilters.filterValues.maxDepartureTime)
      ) {
        isTouched = true;
      }
    });

    const nextDepartureTime: IDepartureTime = deepCloneObject(departureTime);

    if (nextDepartureTime.touched !== isTouched) {
      nextDepartureTime.touched = isTouched;
    }

    if (typeof newValue === "object") {
      if (nextDepartureTime.departureTimeRanges[filterIndex].showDeparture) {
        nextDepartureTime.departureTimeRanges[filterIndex].departure = newValue;
      } else {
        nextDepartureTime.departureTimeRanges[filterIndex].arrival = newValue;
      }

      const departureTime = `${minutesToClock(
        nextDepartureTime.departureTimeRanges[0].departure[0],
      )} - ${minutesToClock(
        nextDepartureTime.departureTimeRanges[0].departure[1],
      )}`;
      const arrivalTime = `${minutesToClock(
        nextDepartureTime.departureTimeRanges[0].arrival[0],
      )} - ${minutesToClock(
        nextDepartureTime.departureTimeRanges[0].arrival[1],
      )}`;

      pushToDataLayer(
        `departure: ${departureTime}, arrival: ${arrivalTime}`,
        "Filter - Departure time",
      );

      dispatch(changeDepartureTime(nextDepartureTime));
    }
  };

  const changeShowDeparture = (index: number, showDeparture: boolean) => {
    let nextDepartureTime: IDepartureTime = deepCloneObject(departureTime);
    if (
      nextDepartureTime.departureTimeRanges[index].showDeparture !==
      showDeparture
    ) {
      nextDepartureTime.departureTimeRanges[index].showDeparture =
        showDeparture;
    }
    setDepartureTimeState(nextDepartureTime.departureTimeRanges);

    dispatch(changeDepartureTime(nextDepartureTime));
  };

  const getFlightIndexHeader = (index: number) => {
    return (
      <Typography variant="titleSmall" weight="bold">
        {tripType === "MULTI_DEST"
          ? `${index + 1}. ${multiFlightText}`
          : `${index === 0 ? outboundText : returnText}`}
      </Typography>
    );
  };

  const getMarks = (index: number, showDeparture: boolean) => {
    const values = showDeparture
      ? departureTimeState[index].departure
      : departureTimeState[index].arrival;
    return [
      {
        value: 0,
        label: minutesToClock(values[0]),
      },
      {
        value: Number(translationsFilters.filterValues.maxDepartureTime),
        label: minutesToClock(values[1]),
      },
    ];
  };

  return (
    <>
      {departureTimeState.map((range, index) => {
        return (
          <Collapse
            key={`transition-${index}`}
            in={true}
            easing={transitions.easing.easeIn}
            timeout={transitions.duration.shortest}
            sx={sxStyles.collapse}
          >
            <Box sx={sxStyles.filter} key={`departure-time-filter-${index}`}>
              {getFlightIndexHeader(index)}
              <Box sx={sxStyles.buttons}>
                <Button
                  onClick={() => changeShowDeparture(index, true)}
                  variant="text"
                  edge="start"
                  sx={{
                    ...sxStyles.button,
                    opacity: range.showDeparture ? 1 : 0.33,
                  }}
                >
                  <Typography variant="titleMedium" weight="bold">
                    {departureTitle}
                  </Typography>
                </Button>
                <Button
                  onClick={() => changeShowDeparture(index, false)}
                  variant="text"
                  edge="start"
                  sx={{
                    ...sxStyles.button,
                    opacity: range.showDeparture ? 0.33 : 1,
                  }}
                >
                  <Typography variant="titleMedium" weight="bold">
                    {arrivalTitle}
                  </Typography>
                </Button>
              </Box>

              <Box sx={sxStyles.rangeSlider}>
                <Slider
                  getAriaValueText={(value: number, index: number) => {
                    return minutesToClock(value);
                  }}
                  onChange={(event: Event, newValue: number | number[]) =>
                    handleChangeSlider(event, newValue, index)
                  }
                  onChangeCommitted={(
                    event: Event | SyntheticEvent<Element, Event>,
                    newValue: number | number[],
                  ) => handleChangeSliderComitted(event, newValue, index)}
                  value={range.showDeparture ? range.departure : range.arrival}
                  min={0}
                  max={Number(
                    translationsFilters.filterValues.maxDepartureTime,
                  )}
                  step={15}
                  valueLabelDisplay="auto"
                  marks={getMarks(index, range.showDeparture)}
                  valueLabelFormat={(value: number, index: number) => {
                    return minutesToClock(value);
                  }}
                />
              </Box>
            </Box>
          </Collapse>
        );
      })}
    </>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  collapse: {
    borderBottom: "1px solid",
    borderBottomColor: "grey.300",
    "&:last-of-type": {
      borderBottom: "0px solid",
    },
  },
  filter: {
    my: 1,
    py: 1,
  },
  buttons: {
    display: "flex",
    justifyContent: "left",
  },
  button: {
    mr: 2,
    textTransform: "capitalize",
    "&.inactive": {
      opacity: 0.33,
    },
  },
  rangeSlider: {
    borderBottom: "1px solid",
    borderBottomColor: "grey.200",
    "&:last-of-type": {
      borderBottom: "0px solid",
    },
    "& .MuiSlider-root": {
      color: "grey.600",
    },
    "& .MuiSlider-rail": {
      color: "grey.400",
      padding: "2px 0 0 0",
    },
    "& .MuiSlider-thumb": {
      backgroundColor: "white.main",
    },
    "& .MuiSlider-valueLabel": {
      backgroundColor: "primary.main",
      py: 0.35,
      px: 0.75,
    },
    "& .MuiSlider-mark": {
      display: "none",
    },
    "& .MuiSlider-markLabel": {
      "&[data-index='0']": {
        transform: "translateX(0)",
      },
      "&[data-index='1']": {
        transform: "translateX(-100%)",
      },
    },
  },
};
